import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EloBadge from "../common/EloBadge.js";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { getPendingRequestRecipientSteamId } from "../../utils/friendRequests";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
    requestRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.28rem',
        padding: '0.26rem 0.3rem',
        marginBottom: '0.2rem',
        background: 'transparent',
        borderRadius: '0',
        border: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.03)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.3rem 0.34rem',
            marginBottom: '0.25rem',
            borderRadius: '0',
        },
    },
    requestRowSlidingOut: {
        transform: 'translateX(-100%)',
        opacity: 0,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
    },
    requestRowSlidingOutRight: {
        transform: 'translateX(100%)',
        opacity: 0,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
    },
    requestPlayerInfoContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    requestPlayerInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    avatarContainer: {
        position: 'relative',
        flexShrink: 0,
    },
    avatar: {
        width: '1.8rem',
        height: '1.8rem',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        [theme.breakpoints.up("xl")]: {
            width: '1.95rem',
            height: '1.95rem',
        },
    },
    requestIndicator: {
        position: 'absolute',
        top: '-2px',
        right: '0px',
        width: '13px',
        height: '13px',
        borderRadius: '50%',
        background: '#E8A33D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.6rem',
        color: '#ffffff',
        fontWeight: 'bold',
        boxShadow: 'none',
        [theme.breakpoints.up("xl")]: {
            width: '16px',
            height: '16px',
            fontSize: '0.72rem',
        },
    },
    requestNameContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    requestInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        minWidth: 0,
        marginRight: '0.4rem',
    },
    requestName: {
        color: '#ffffff',
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.76rem',
        },
    },
    requestText: {
        color: '#9fa7b0',
        fontSize: '0.64rem',
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: '1.3',
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.68rem',
        },
    },
    eloContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.22rem',
        flexShrink: 0,
        [theme.breakpoints.up("xl")]: {
            gap: '0.26rem',
        },
    },
    acceptButton: {
        background: 'rgba(56, 176, 0, 0.16)',
        color: '#ffffff',
        fontSize: '0.64rem',
        fontWeight: 600,
        padding: '0.1rem 0.45rem',
        borderRadius: '5px',
        border: '1px solid rgba(56, 176, 0, 0.55)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        textTransform: 'none',
        letterSpacing: '0.2px',
        boxShadow: 'none',
        minWidth: '52px',
        minHeight: '28px',
        '&:hover': {
            background: 'rgba(56, 176, 0, 0.26)',
            boxShadow: 'none',
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.5rem',
            fontSize: '0.68rem',
            minWidth: '56px',
        },
    },
    declineButton: {
        background: 'rgba(220, 20, 60, 0.16)',
        color: '#ffffff',
        fontSize: '0.64rem',
        fontWeight: 600,
        padding: '0.1rem 0.45rem',
        borderRadius: '5px',
        border: '1px solid rgba(220, 20, 60, 0.52)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        textTransform: 'none',
        letterSpacing: '0.2px',
        boxShadow: 'none',
        minWidth: '52px',
        minHeight: '28px',
        '&:hover': {
            background: 'rgba(220, 20, 60, 0.26)',
            boxShadow: 'none',
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.5rem',
            fontSize: '0.68rem',
            minWidth: '56px',
        },
    },
    menuButton: {
        color: '#cfd4d9',
        padding: '0.1rem',
        minWidth: '28px',
        minHeight: '28px',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.14rem',
        },
    },
    menuButtonIcon: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.75rem',
        },
    },
    menu: {
        zIndex: 9999,
        padding: 0,
        '& .MuiPaper-root': {
            background: '#2A2D30',
            minWidth: '8rem',
            border: '1px solid rgba(232, 163, 61, 0.2)',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.up("xl")]: {
                minWidth: '10rem',
            },
        }
    },
    menuItem: {
        width: '100%',
        color: '#ffffff',
        fontSize: '0.75rem',
        padding: '0.5rem 1.8rem',
        textTransform: 'uppercase',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            padding: '0.65rem 2.25rem',
            fontSize: '0.9rem',
        },
    },
    menuItemProfile: {
        backgroundColor: '#1E90FF',
        '&:hover': {
            backgroundColor: '#0073da',
        }
    },
    menuItemBlock: {
        marginTop: '0.4rem',
        backgroundColor: '#666666',
        '&:hover': {
            backgroundColor: '#444444',
        }
    }
}));

export default function RequestRow({ request, handleAcceptFriendRequest, handleDeclineFriendRequest, friendRequestToCancel }) {
    const classes = useStyles();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [acceptSlideAnimation, setAcceptSlideAnimation] = useState(false);
    const [declineSlideAnimation, setDeclineSlideAnimation] = useState(false);
    const history = useHistory();
    const recipientSteamId = getPendingRequestRecipientSteamId(request);
    
    const avatarSrc = resolveAvatarSrc({
      basePath,
      picture: request.fromUser?.picture,
      userId: request.fromUser?.userId || request.fromUser?._id,
      avatarUpdatedAtMs: pickAvatarUpdatedAtMs(request.fromUser),
      hasCustomAvatar: !!request.fromUser?.avatar,
    });
    
    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    
    const handleAccept = () => {
        if (!recipientSteamId) {
            return;
        }
        Promise.resolve(
            handleAcceptFriendRequest(request.fromUser.steamId, recipientSteamId)
        )
            .then((result) => {
                if (!result?.ok) {
                    return;
                }
                setAcceptSlideAnimation(true);
                setTimeout(() => {
                    friendRequestToCancel(request._id);
                    setAcceptSlideAnimation(false);
                }, 600);
            })
            .catch(() => {});
    };
    
    const handleDecline = () => {
        if (!recipientSteamId) {
            return;
        }
        Promise.resolve(
            handleDeclineFriendRequest(request.fromUser.steamId, recipientSteamId)
        )
            .then((result) => {
                if (!result?.ok) {
                    return;
                }
                setDeclineSlideAnimation(true);
                setTimeout(() => {
                    friendRequestToCancel(request._id);
                    setDeclineSlideAnimation(false);
                }, 600);
            })
            .catch(() => {});
    };
    
    const handleProfile = () => {
        history.push(`/profile/${request.fromUser._id}`);
    };
    
    // const handleBlock = () => {
    //     console.log('Block user:', request._id);
    // };

    return (
        <div className={`${classes.requestRow} ${
            acceptSlideAnimation ? classes.requestRowSlidingOut : 
            declineSlideAnimation ? classes.requestRowSlidingOutRight : ''
        }`}>
            <div className={classes.requestPlayerInfoContainer}>
                <div className={classes.requestPlayerInfo}>
                    <div className={classes.avatarContainer}>
                        <Avatar
                            className={classes.avatar}
                            src={avatarSrc}
                            alt={request.fromUser?.nickname || "Unknown User"}
                        />
                        <div className={classes.requestIndicator}>
                            !
                        </div>
                    </div>
                    <div className={classes.requestNameContainer}>
                        <Typography className={classes.requestName}>
                            {request.fromUser?.nickname || "Unknown User"}
                        </Typography>
                        <div className={classes.eloContainer}>
                            <EloBadge value={request.fromUser?.elo} size="small" />
                        </div>
                    </div>
                </div>
                <div className={classes.actionsContainer}>
                    <IconButton
                        className={classes.menuButton}
                        onClick={handleMenuOpen}
                        size="small"
                    >
                        <MoreVertIcon className={classes.menuButtonIcon} />
                    </IconButton>

                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleMenuClose}
                        className={classes.menu}
                        disableScrollLock={true}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem 
                            className={`${classes.menuItem} ${classes.menuItemProfile}`}
                            onClick={handleProfile}
                        >
                            Profile
                        </MenuItem>
                        {/* <MenuItem 
                            className={`${classes.menuItem} ${classes.menuItemBlock}`}
                            onClick={handleBlock}
                        >
                            Block
                        </MenuItem> */}
                    </Menu>
                </div>
            </div>
            
            
            <div className={classes.requestInfo}>
            <Typography className={classes.requestText}>
                        {request.message || "Wants to add you as a friend."}
                    </Typography>
            </div>
            
            <div className={classes.actionsContainer}>
                <Button
                    className={classes.acceptButton}
                    onClick={handleAccept}
                    startIcon={<CheckIcon />}
                >
                    Accept
                </Button>
                
                <Button
                    className={classes.declineButton}
                    onClick={handleDecline}
                    startIcon={<CloseIcon />}
                >
                    Decline
                </Button>
            </div>
        </div>
    );
}